/**
 * apps/web :: lib/admin-api
 *
 * Typed wrappers over the `/api/admin/practice/questions` surface shipped
 * by PR #122 (ROADS-306) and wired by PR #124 (ROADS-308). This is the
 * content-ops surface — auth'd via the standard `rb_session` cookie, the
 * backend gate-checks the user id against the env-driven AdminAllowlist,
 * and a missing admin module (dev, local, staging without the env var)
 * responds with 503 on every verb.
 *
 * Wire shape: snake_case via `@SerialName` on compound fields of
 * `PracticeQuestion` (state_code, question_text, correct_choice_id,
 * handbook_section, reviewed_by, reviewed_at_ms, created_at_ms). The list
 * response carries `totalCount` as pure camelCase (per PracticeAdminRoutes
 * `AdminListResponse`). These wrappers leave the wire shape intact on the
 * practice record itself (mirrors `PracticeQuestionForClient` treatment in
 * practice-api.ts) while normalising the list envelope to `total_count`
 * so consumer components only see one convention.
 *
 * PII hygiene:
 *   - `ApiError.message` is whatever the server sent. The backend NEVER
 *     echoes `question_text` / `explanation` back in 400 payloads — the
 *     invalid_field response carries only {field, message}. We still do
 *     not log the outgoing payload anywhere in this module.
 *   - `probeAdminAccess()` swallows 401/403/503 into stable string labels
 *     instead of throwing — the AdminGate caller wants to branch on this,
 *     not try/catch three separate statuses.
 */
import { ApiError, apiFetch } from "@/lib/api";

/** Single admin-view practice question. Includes correct_choice_id + explanation
 *  (unlike the user-facing PracticeQuestionForClient). Mirrors the backend
 *  `PracticeQuestion` wire shape — see
 *  `kmp-shared-core/.../practice/PracticeQuestion.kt`. */
export interface AdminPracticeQuestion {
  id: string;
  state_code: string;
  locale: string;
  /** UPPER_SNAKE category name as emitted by the wire, e.g. "RIGHT_OF_WAY". */
  category: string;
  question_text: string;
  choices: Array<{ id: string; text: string }>;
  correct_choice_id: string;
  explanation: string;
  handbook_section: string | null;
  difficulty: number;
  reviewed_by: string | null;
  reviewed_at_ms: number | null;
  created_at_ms: number;
}

/** Payload shape for create (POST) — the record minus `id` and
 *  `created_at_ms` (server assigns both). */
export type AdminPracticeQuestionInput = Omit<
  AdminPracticeQuestion,
  "id" | "created_at_ms"
>;

/** Filter + paging query-string inputs for the list surface. */
export interface AdminListFilters {
  state?: string;
  category?: string;
  locale?: string;
  limit?: number;
  offset?: number;
}

/** Parsed list response — normalised camelCase `totalCount` → snake_case. */
export interface AdminListPage {
  questions: AdminPracticeQuestion[];
  total_count: number;
}

/** Stable classifier for the mount-time probe. Narrowing this at the call
 *  site lets AdminGate route to the correct redirect / error surface
 *  without re-checking HTTP codes. */
export type AdminAccess = "admin" | "not_admin" | "unauth" | "unavailable";

/** Shared path for the admin surface. Kept private so tests can't accidentally
 *  depend on the literal; change it once if the backend ever relocates. */
const ADMIN_BASE = "/api/admin/practice/questions";

/**
 * Probe admin access with a cheap `limit=0` list call. Classifies the four
 * auth outcomes and propagates any unexpected HTTP error. Cheap because
 * the backend just runs its in-memory filter then slices 0 rows — no disk
 * hit in the happy path.
 */
export async function probeAdminAccess(): Promise<AdminAccess> {
  try {
    await apiFetch<AdminListPage>(`${ADMIN_BASE}?limit=0`);
    return "admin";
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) return "unauth";
      if (err.status === 403) return "not_admin";
      if (err.status === 503) return "unavailable";
    }
    throw err;
  }
}

/** List paginated admin questions with optional filters. */
export async function listAdminQuestions(
  filters: AdminListFilters,
): Promise<AdminListPage> {
  const params = new URLSearchParams();
  if (filters.state) params.set("state", filters.state);
  if (filters.category) params.set("category", filters.category);
  if (filters.locale) params.set("locale", filters.locale);
  if (typeof filters.limit === "number") {
    params.set("limit", String(filters.limit));
  }
  if (typeof filters.offset === "number") {
    params.set("offset", String(filters.offset));
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const wire = await apiFetch<{
    questions: AdminPracticeQuestion[];
    totalCount: number;
  }>(`${ADMIN_BASE}${suffix}`);
  return {
    questions: wire.questions ?? [],
    total_count: wire.totalCount ?? 0,
  };
}

/** Create a new practice question. Returns the server-assigned record. */
export async function createAdminQuestion(
  payload: AdminPracticeQuestionInput,
): Promise<AdminPracticeQuestion> {
  return apiFetch<AdminPracticeQuestion>(ADMIN_BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update an existing question. Partial bodies accepted by the backend. */
export async function updateAdminQuestion(
  id: string,
  patch: AdminPracticeQuestionInput,
): Promise<AdminPracticeQuestion> {
  return apiFetch<AdminPracticeQuestion>(`${ADMIN_BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

/** Delete a question. Cascades to any practice_attempts referencing it. */
export async function deleteAdminQuestion(id: string): Promise<void> {
  await apiFetch<void>(`${ADMIN_BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Bulk-import surface (Wave 7b, ROADS-311)
// ---------------------------------------------------------------------------

/** A successfully parsed CSV row, surfaced in the preview response. */
export interface ImportPreviewRow {
  rowNumber: number;
  state: string;
  topicCode: string;
  difficulty: string;
  questionText: string;
  choiceA: string;
  choiceB: string;
  choiceC: string;
  choiceD: string;
  correctIndex: number;
  explanation: string;
  sourceHandbookSection: string | null;
  locale: string;
}

/** A single field-level validation failure on a row in the previewed CSV. */
export interface ImportPreviewError {
  rowNumber: number;
  field: string;
  message: string;
}

/** Server-side summary: tells the UI whether commit can proceed. */
export interface ImportPreviewSummary {
  rowsParsed: number;
  rowsValid: number;
  rowsInvalid: number;
}

/** Full preview response. Token is opaque — pass it back unchanged on commit. */
export interface ImportPreview {
  token: string;
  summary: ImportPreviewSummary;
  errors: ImportPreviewError[];
  rows: ImportPreviewRow[];
}

/** 1 MiB cap, mirrors the backend `BULK_IMPORT_MAX_BYTES`. */
export const BULK_IMPORT_MAX_BYTES = 1 * 1024 * 1024;

const IMPORT_PREVIEW_PATH = `${ADMIN_BASE}/import/preview`;
const IMPORT_COMMIT_PATH = `${ADMIN_BASE}/import/commit`;

/**
 * Upload a CSV for preview. The backend never persists the rows at this
 * step — it parses, validates, stages the result against a UUID token,
 * and returns everything the UI needs to render the preview table.
 *
 * The Content-Type header is intentionally omitted — `fetch` synthesises
 * the multipart boundary header from the FormData body.
 */
export async function previewImport(file: File): Promise<ImportPreview> {
  if (file.size > BULK_IMPORT_MAX_BYTES) {
    throw new ApiError(
      413,
      `CSV upload must be ≤ ${BULK_IMPORT_MAX_BYTES / 1024} KiB`,
      { error: "file_too_large" },
    );
  }
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<ImportPreview>(IMPORT_PREVIEW_PATH, {
    method: "POST",
    body: fd,
  });
}

/**
 * Commit a previously-staged preview. The backend only accepts tokens
 * with zero `errors`; otherwise it returns 409 (validation_errors_present)
 * which we surface unchanged so callers can decide how to render.
 */
export async function commitImport(
  token: string,
): Promise<{ rowsInserted: number }> {
  return apiFetch<{ rowsInserted: number }>(IMPORT_COMMIT_PATH, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}
